import { EmptyStateSMS } from 'ui/illustrations';
import { SMSContentDefinition } from 'modules/graphql';
import { Message } from './Message';
import { Notification } from './Notification';
import { ContactCardDetails } from './ContactCardDetails';
import {
  containerStyles,
  deviceStyles,
  deviceStylesCommon,
  deviceStylesUnset,
  messageUnset,
} from './styles';
import { displayContent } from '../../common/content';
import { OS, PreviewType, TemplateEditViews } from '../../common/types';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { useSMSPreviewContext } from '../context/SMSPreviewContext';
import { useQueryParamContext } from '../../context/QueryParamContext';

type Props = {
  selectedOs: OS;
  selectedPreviewType: PreviewType;
};

const renderPreview = (selectedOs: OS, selectedPreviewType: PreviewType) => {
  if (selectedPreviewType === PreviewType.message) {
    return <Message data-test="message" selectedOs={selectedOs} />;
  }
  if (selectedPreviewType === PreviewType.notification) {
    return <Notification data-test="notification" selectedOs={selectedOs} />;
  }
  if (selectedPreviewType === PreviewType.contactCard) {
    return <ContactCardDetails data-test="contact-card" />;
  }
  return null;
};

export const Preview = ({ selectedOs, selectedPreviewType }: Props) => {
  /**
   * only show the empty state if neither the message in the template data
   * nor contact card are not defined, even if the message is a snippet which
   * resolves to an empty string.
   */
  const { view } = useQueryParamContext();
  const { hasContent, liveTemplate } = useEditableFieldsContext();
  const { previewError: hasPreviewError } = useSMSPreviewContext();
  const contactCardId = (liveTemplate.mediumData as SMSContentDefinition)?.verifiedContactCardId;
  const isContactCardSelected = contactCardId && contactCardId > -1;
  // need preview to only be hidden when there's errors and it's the preview view
  const shouldHidePreview =
    (hasPreviewError && view === TemplateEditViews.preview) ||
    (!isContactCardSelected && !hasContent);

  return (
    <div style={containerStyles}>
      <div
        data-test="template-editor-sms-preview"
        style={{
          ...deviceStylesCommon,
          ...(!shouldHidePreview
            ? deviceStyles[selectedOs][selectedPreviewType]
            : deviceStylesUnset),
        }}
      >
        {!shouldHidePreview ? (
          renderPreview(selectedOs, selectedPreviewType)
        ) : (
          <>
            <EmptyStateSMS data-test="empty-state-sms" />
            {!hasPreviewError && (
              <div data-test="empty-content" style={messageUnset}>
                {displayContent.strEmptyContentSMS}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Preview;
